import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createSubmitAttempts,
  formToken,
  resolveForm,
  submitForm,
} from "./api";
import { FormRenderer, type Answers } from "./FormRenderer";
import {
  AlertIcon,
  CheckCircleIcon,
  InfoIcon,
  LockIcon,
} from "./icons";
import { STRINGS } from "./strings";
import type { ResolvedForm } from "./types";

type Phase = "loading" | "ready" | "done";
/** Mirrors ResolveOutcome, plus the state before the first attempt returns. */
type Status = "loading" | "ok" | "closed" | "error";

const EMPTY_FORM: ResolvedForm = {
  ok: false,
  title: null,
  description: null,
  successMessage: null,
  fields: [],
  prefill: {},
  answers: null,
  locale: "en",
  branding: null,
  canSubmit: false,
  canEdit: false,
  submitted: false,
  editsRemaining: 0,
};

/**
 * Apply the organization's colour without shipping a theming system.
 *
 * Mirrors what apps/widget/components/theme-injector.tsx does, minus the OKLCH
 * conversion: this page has one accent and one button, so a CSS variable is the
 * whole requirement.
 */
function useBrandColor(color: string | null | undefined) {
  useEffect(() => {
    if (!color || !/^#[0-9a-fA-F]{3,8}$/.test(color)) return;
    document.documentElement.style.setProperty("--brand", color);
  }, [color]);
}

/**
 * The organisation's palette.
 *
 * Every theme ships in this bundle, so the payload carries only an id. An
 * unknown or absent id leaves the attribute unset and the default palette --
 * declared on :root -- applies, which is what a form published before the theme
 * engine existed gets.
 */
function useOrgTheme(themeId: string | null | undefined) {
  useEffect(() => {
    if (!themeId || !/^[a-z0-9-]{1,32}$/.test(themeId)) return;
    document.documentElement.dataset.theme = themeId;
    return () => {
      delete document.documentElement.dataset.theme;
    };
  }, [themeId]);
}

function useDirection(locale: "en" | "ar") {
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);
}

export default function App() {
  const token = useMemo(() => formToken(window.location.pathname), []);
  const [phase, setPhase] = useState<Phase>("loading");
  const [status, setStatus] = useState<Status>("loading");
  const [form, setForm] = useState<ResolvedForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [submitAttempts] = useState(() => createSubmitAttempts());

  const strings = STRINGS[form.locale];
  useOrgTheme(form.branding?.themeId);
  useBrandColor(form.branding?.primaryColor);
  useDirection(form.locale);

  useEffect(() => {
    let cancelled = false;
    // A path that is not `/f/<token>` never had a link to check.
    if (!token) {
      setStatus("closed");
      setPhase("ready");
      return;
    }
    setStatus("loading");
    void resolveForm(token).then((outcome) => {
      if (cancelled) return;
      if (outcome.status === "ok") setForm(outcome.form);
      setStatus(outcome.status);
      setPhase("ready");
    });
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const handleSubmit = async (answers: Answers) => {
    if (!token) return;
    setSubmitting(true);
    setErrors({});
    setNotice(null);
    try {
      const result = await submitForm(token, answers, {
        attemptId: submitAttempts.idFor(answers),
        baseRevision: form.revision,
      });
      // Only a lost response keeps the attempt; any verdict ends it.
      if (result.code !== "NETWORK") submitAttempts.settle();
      if (result.ok) {
        setPhase("done");
        setNotice(result.successMessage ?? strings.thanks);
        return;
      }
      if (result.code === "INVALID") {
        setErrors(
          Object.fromEntries(
            result.errors.map((error) => [
              error.key,
              strings.errors[error.reason] ?? strings.problem,
            ]),
          ),
        );
        setNotice(strings.fixErrors);
        return;
      }
      // The link died between loading the page and submitting it — someone
      // cancelled the form, or the edit was used on another device.
      setNotice(
        result.code === "NO_EDITS_LEFT"
          ? strings.noEditsLeft
          : result.code === "STALE"
            ? strings.stale
            : result.code === "UNAVAILABLE"
            ? strings.unavailable
            : strings.problem,
      );
      if (result.code !== "NETWORK") {
        setForm((current) => ({ ...current, canSubmit: false }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "loading" || status === "loading") {
    // A skeleton in the shape of the card that is coming, rather than a line of
    // text that reflows the whole page the moment it is replaced.
    return (
      <main className="page">
        <div className="card" role="status" aria-label={strings.loading}>
          <div className="skeleton-stack">
            <div className="skeleton" style={{ height: 14, width: "35%" }} />
            <div className="skeleton" style={{ height: 22, width: "70%" }} />
            <div className="skeleton" style={{ height: 44 }} />
            <div className="skeleton" style={{ height: 44 }} />
            <div className="skeleton" style={{ height: 48 }} />
          </div>
        </div>
      </main>
    );
  }

  // We never reached the server, so we have no idea whether the link is good.
  // Saying it is dead would be a guess, and it is the guess that hides our own
  // outages behind a message the customer can do nothing about.
  if (status === "error") {
    return (
      <main className="page">
        <div className="card">
          <div className="card-header">
            <AlertIcon className="state-icon is-danger" />
            <h1 className="title">{strings.errorTitle}</h1>
            <p className="muted">{strings.errorBody}</p>
          </div>
          <div className="state-actions">
            <button className="submit" type="button" onClick={retry}>
              {strings.retry}
            </button>
          </div>
        </div>
      </main>
    );
  }

  // Expired, cancelled, already finished, or never real — all the same page, so
  // this never becomes a way to test whether a guessed token exists.
  if (status === "closed") {
    return (
      <main className="page">
        <div className="card">
          <div className="card-header">
            <LockIcon className="state-icon" />
            <h1 className="title">{strings.closedTitle}</h1>
            <p className="muted">{strings.closedBody}</p>
          </div>
        </div>
      </main>
    );
  }

  const done = phase === "done";
  const editing = form.submitted && form.canEdit;

  return (
    <main className="page">
      <div className="card">
        <div className="card-header">
          {form.branding?.logoUrl ? (
            <img
              className="logo"
              src={form.branding.logoUrl}
              alt={form.branding.companyName ?? ""}
            />
          ) : form.branding?.companyName ? (
            <p className="brand">{form.branding.companyName}</p>
          ) : null}

          <h1 className="title">{form.title}</h1>
          {form.description ? (
            <p className="muted">{form.description}</p>
          ) : null}
        </div>

        {done ? (
          <p className="success" role="status">
            <CheckCircleIcon className="banner-icon" />
            <span>{notice}</span>
          </p>
        ) : (
          <>
            {editing ? (
              <p className="banner">
                <InfoIcon className="banner-icon" />
                <span>{strings.editingNow}</span>
              </p>
            ) : form.submitted ? (
              <p className="banner">
                <InfoIcon className="banner-icon" />
                <span>{strings.noMoreEdits}</span>
              </p>
            ) : null}
            {notice ? (
              <p className="error-banner" role="alert">
                <AlertIcon className="banner-icon" />
                <span>{notice}</span>
              </p>
            ) : null}
            <FormRenderer
              fields={form.fields}
              locale={form.locale}
              strings={strings}
              initial={form.prefill as Answers}
              disabled={!form.canSubmit}
              submitting={submitting}
              submitLabel={editing ? strings.saveChanges : strings.submit}
              errors={errors}
              onSubmit={handleSubmit}
            />
            {/* Only promise a correction when one is actually owed — the
                default is that submitting locks the form. */}
            {!form.submitted ? (
              <p className="footnote">
                {form.editsRemaining > 0
                  ? strings.canEditOnce
                  : strings.submitIsFinal}
              </p>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
}
